// src/context/ScenarioContext.js
import React, { createContext, useState, useContext, useEffect } from 'react';
import { useBioData } from './BioDataContext';
import { useUser } from './UserContext';
import { getNextColor } from '../utils/colorUtils';
import { getUserScenarios } from '../services/firestore';

const ScenarioContext = createContext();

export function useScenarios() {
  return useContext(ScenarioContext);
}

export function ScenarioProvider({ children }) {
  const { calculateRetirementStats } = useBioData();
  const { user } = useUser();
  const currentYear = new Date().getFullYear();
  
  const [nextScenarioId, setNextScenarioId] = useState(2);
  
  const [scenarios, setScenarios] = useState([
    {
      id: 1,
      name: "Scenario 1",
      retirementYear: currentYear + 20,
      retirementAge: null, // Calculated from DOB
      yearsOfService: null, // Calculated from EOD
      survivorBenefit: 50,
      pensionType: 'standard',
      departureYear: 2025, // Default departure year set to 2025
      deferredRetirementAge: 62, // Default deferred retirement age
      tspContribution: 5, // Default 5%
      agencyMatch: 5, // Default agency match
      withdrawalRate: 4,
      customWithdrawal: false,
      withdrawalStartYear: currentYear + 21,
      withdrawalType: 'percentage',
      withdrawalAmount: 4,
      withdrawalDollarAmount: 0,
      promotions: [],
      includeColaForSalary: false,
      isFinalized: false,
      additionalIncomes: [],
      visible: true,
      expanded: true, // Default scenario starts expanded
      color: '#4285F4' // Google blue
    }
  ]);

  // Load scenarios when user logs in
  useEffect(() => {
    const loadScenarios = async () => {
      if (user && !user.isGuest) {
        try {
          const savedScenarios = await getUserScenarios(user.uid);
          if (savedScenarios && savedScenarios.length > 0) {
            // Ensure all loaded scenarios have the visible property set to true
            const scenariosWithVisibility = savedScenarios.map(s => ({
              ...s,
              visible: s.visible !== undefined ? s.visible : true,
              expanded: s.expanded !== undefined ? s.expanded : true
            }));
            setScenarios(scenariosWithVisibility);
            // Update nextScenarioId to be higher than any existing scenario ID
            const maxId = Math.max(...savedScenarios.map(s => parseInt(s.id) || 0));
            setNextScenarioId(maxId + 1);
          }
        } catch (error) {
          console.error('Error loading scenarios:', error);
        }
      }
    };

    loadScenarios();
  }, [user]);

  const handleScenarioChange = (scenarioId, field, value) => {
    setScenarios(prev => 
      prev.map(scenario => {
        if (scenario.id === scenarioId) {
          // If changing retirement year, also update calculated fields
          if (field === 'retirementYear') {
            const stats = calculateRetirementStats(value);
            return { 
              ...scenario, 
              [field]: value,
              retirementAge: stats.retirementAge,
              yearsOfService: stats.yearsOfService
            };
          }
          
          // If changing to deferred pension type, set departure date to Dec 31, 2025
          if (field === 'pensionType' && value === 'deferred') {
            const deferredDate = '2025-12-31';
            return { 
              ...scenario, 
              [field]: value,
              departureYear: 2025,
              lastDayOfService: deferredDate
            };
          }
          
          return { ...scenario, [field]: value };
        }
        return scenario;
      })
    );
  };

  const toggleScenarioVisibility = (scenarioId) => {
    // Since visibility toggle was removed from UI, always ensure scenarios are visible
    setScenarios(prev => 
      prev.map(scenario => 
        scenario.id === scenarioId 
          ? { ...scenario, visible: true } // Always set to true
          : scenario
      )
    );
  };
  
  const toggleScenarioExpanded = (scenarioId) => {
    setScenarios(prev => 
      prev.map(scenario => 
        scenario.id === scenarioId 
          ? { ...scenario, expanded: !scenario.expanded } 
          : scenario
      )
    );
  };
  
  const addScenario = () => {
    // Get the last scenario as a template
    const templateScenario = scenarios[scenarios.length - 1];
    
    // Create new scenario based on template
    const newScenario = {
      ...templateScenario,
      id: nextScenarioId,
      name: `Scenario ${nextScenarioId}`,
      promotions: [...templateScenario.promotions], // Deep copy promotions
      expanded: true, // New scenarios start expanded
      visible: true, // Always visible since toggle was removed from UI
      color: getNextColor(scenarios.length)
    };
    
    setScenarios([...scenarios, newScenario]);
    setNextScenarioId(nextScenarioId + 1);
  };

  const removeScenario = (scenarioId) => {
    if (scenarios.length <= 1) {
      alert("Can't remove the only scenario");
      return;
    }
    
    setScenarios(prev => prev.filter(scenario => scenario.id !== scenarioId));
  };
  
  // Promotion management
  const addPromotion = (scenarioId, year, grade) => {
    setScenarios(prev => 
      prev.map(scenario => {
        if (scenario.id === scenarioId) {
          return {
            ...scenario,
            promotions: [...scenario.promotions, { year, grade }]
          };
        }
        return scenario;
      })
    );
  };

  const removePromotion = (scenarioId, index) => {
    setScenarios(prev => 
      prev.map(scenario => {
        if (scenario.id === scenarioId) {
          const newPromotions = [...scenario.promotions];
          newPromotions.splice(index, 1);
          return {
            ...scenario,
            promotions: newPromotions
          };
        }
        return scenario;
      })
    );
  };
  
  // Toggle income source inclusion for a specific scenario
  const toggleIncomeForScenario = (incomeId, scenarioId, isEnabled) => {
    setScenarios(prev => {
      return prev.map(scenario => {
        // Only update the specific scenario
        if (scenario.id === scenarioId) {
          // If the scenario doesn't have an includedIncomes array, create one
          const includedIncomes = scenario.includedIncomes || [];
          
          // Add or remove the income ID based on isEnabled
          if (isEnabled && !includedIncomes.includes(incomeId)) {
            // Add the income to the scenario
            return {
              ...scenario,
              includedIncomes: [...includedIncomes, incomeId]
            };
          } else if (!isEnabled && includedIncomes.includes(incomeId)) {
            // Remove the income from the scenario
            return {
              ...scenario,
              includedIncomes: includedIncomes.filter(id => id !== incomeId)
            };
          } else {
            // This ensures the function always returns an updated scenario object
            // even if no change to includedIncomes was needed
            return {
              ...scenario,
              includedIncomes: [...includedIncomes]
            };
          }
        }
        return scenario;
      });
    });
  };
  
  // Helper function to check if an income is included in a scenario
  const isIncomeIncludedInScenario = (incomeId, scenarioId) => {
    const scenario = scenarios.find(s => s.id === scenarioId);
    if (!scenario) return false;
    
    // If the scenario doesn't have includedIncomes yet, initialize it
    if (!scenario.includedIncomes) {
      return false;
    }
    
    return scenario.includedIncomes.includes(incomeId);
  };
  
  // Update retirement stats when bio data changes
  useEffect(() => {
    // This would be called from BioDataContext changes
    // We'll handle this when we integrate the contexts
  }, []);

  const value = {
    scenarios,
    handleScenarioChange,
    toggleScenarioVisibility,
    toggleScenarioExpanded,
    addScenario,
    removeScenario,
    addPromotion,
    removePromotion,
    toggleIncomeForScenario,
    isIncomeIncludedInScenario
  };

  return (
    <ScenarioContext.Provider value={value}>
      {children}
    </ScenarioContext.Provider>
  );
}